import "../../App.css";
import "../../styles/login.css";
import React, { useEffect, useState, useCallback } from "react";
import { withRouter } from "react-router-dom";
import Sidebar from "../Sidebar";
import UserTab from "../UserTab";
import CurrentUser from "../CurrentUser";
import Parametros from "../Parametros";

const PREVIEW_LIMIT = 50;

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Reporte generico basado en una vista SQL. Recibe:
 * - titulo: nombre humano del reporte
 * - viewName: nombre tecnico de la vista (solo display)
 * - endpointPath: path relativo del endpoint sin el host
 * - mandante: opcional, query param ?mandante=
 * - filenamePrefix: nombre del archivo Excel descargado (sin extension)
 * - dateFilter: si true, muestra inputs Desde / Hasta que se mandan al
 *   backend como fecha_desde / fecha_hasta. Default false.
 */
function ReporteVista(props) {
  const { titulo, viewName, endpointPath, mandante, filenamePrefix, dateFilter, history } = props;

  // Navega al submenu del cliente (Autofin / Global) o al menu principal.
  const handleBack = () => {
    if (mandante) {
      history.push(`/Reporte/${mandante}`);
    } else {
      history.push("/Reporte");
    }
  };
  const [usuario] = useState(CurrentUser.user);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [fechaDesde, setFechaDesde] = useState(todayISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());

  const buildUrl = useCallback(
    (suffix = "") => {
      const base = Parametros.urlApi + endpointPath + suffix;
      const params = [];
      if (mandante) params.push(`mandante=${encodeURIComponent(mandante)}`);
      if (dateFilter) {
        if (fechaDesde) params.push(`fecha_desde=${encodeURIComponent(fechaDesde)}`);
        if (fechaHasta) params.push(`fecha_hasta=${encodeURIComponent(fechaHasta)}`);
      }
      return params.length ? `${base}?${params.join("&")}` : base;
    },
    [endpointPath, mandante, dateFilter, fechaDesde, fechaHasta]
  );

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(buildUrl(), {
      method: "GET",
      headers: {
        Authorization: "Bearer " + usuario.key,
        Accept: "application/json",
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const detail = await res.text();
          throw new Error(detail || "Error al cargar el reporte");
        }
        return res.json();
      })
      .then((rows) => {
        const safe = Array.isArray(rows) ? rows : [];
        setData(safe);
        setColumns(safe.length > 0 ? Object.keys(safe[0]) : []);
      })
      .catch((e) => {
        console.error(e);
        setError("No se pudo cargar el reporte. Intentalo nuevamente.");
      })
      .finally(() => setLoading(false));
  }, [buildUrl, usuario.key]);

  useEffect(() => {
    // Para reportes sin filtro de fecha cargamos automatico al montar.
    // Para reportes con filtro de fecha tambien cargamos automatico la primera
    // vez (con la fecha de hoy en desde/hasta).
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.key, endpointPath, mandante]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(buildUrl("/excel"), {
        method: "GET",
        headers: { Authorization: "Bearer " + usuario.key },
      });
      if (!res.ok) {
        throw new Error("Error al exportar el reporte");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filenamePrefix || "reporte"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(e.message || "Error al exportar a Excel");
    } finally {
      setExporting(false);
    }
  };

  const previewRows = data.slice(0, PREVIEW_LIMIT);

  const formatCell = (v) => {
    if (v === null || v === undefined) return "";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <div className="App">
      <Sidebar tipo="3" />
      <br />
      <UserTab nombre={titulo} />
      <div className="container">
        <div
          className="col-md-12"
          style={{
            marginTop: 16,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <div style={{ marginBottom: 12, width: "100%", textAlign: "left" }}>
            <button
              type="button"
              onClick={handleBack}
              className="btn btn-link"
              style={{
                padding: 0,
                marginBottom: 6,
                color: "#6B5B95",
                textDecoration: "none",
                fontSize: 13,
              }}
            >
              ← Volver
            </button>
            <h3 style={{ margin: 0, textAlign: "left" }}>{titulo}</h3>
            <small style={{ color: "#6b6b6b", textAlign: "left", display: "block" }}>
              {viewName && (
                <>
                  Fuente: <code>{viewName}</code>
                </>
              )}
              {data.length > 0 && (
                <> · {data.length} registro{data.length === 1 ? "" : "s"}</>
              )}
              {data.length > PREVIEW_LIMIT && (
                <> (vista previa: primeras {PREVIEW_LIMIT})</>
              )}
            </small>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "flex-end",
              flexWrap: "wrap",
              marginBottom: 16,
              padding: "12px 14px",
              background: "var(--color-card, #fff)",
              border: "1px solid var(--color-border, #e6e0ee)",
              borderRadius: 8,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {dateFilter && (
              <>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <label style={{ fontSize: 12, color: "#6b6b6b", margin: 0 }}>
                    Desde
                  </label>
                  <input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    className="form-control"
                    style={{ minWidth: 160 }}
                  />
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <label style={{ fontSize: 12, color: "#6b6b6b", margin: 0 }}>
                    Hasta
                  </label>
                  <input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    className="form-control"
                    style={{ minWidth: 160 }}
                  />
                </div>
                <button
                  type="button"
                  onClick={loadData}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? "Buscando..." : "Buscar"}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading || data.length === 0}
              className="btn btn-primary"
              style={{ minWidth: 180, marginLeft: "auto" }}
            >
              {exporting ? "Exportando..." : "Exportar a Excel"}
            </button>
          </div>

          {error && (
            <div className="alert alert-danger" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div
            className="table-responsive"
            style={{
              maxHeight: "60vh",
              overflow: "auto",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <table
              className="table table-bordered table-hover"
              style={{
                background: "#fff",
                marginBottom: 0,
                width: "100%",
                minWidth: "100%",
              }}
            >
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col}
                      style={{
                        background: "#6B5B95",
                        color: "#fff",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={Math.max(columns.length, 1)}
                      style={{ textAlign: "center", padding: 24 }}
                    >
                      Cargando reporte...
                    </td>
                  </tr>
                )}
                {!loading && previewRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={Math.max(columns.length, 1)}
                      style={{ textAlign: "center", padding: 24 }}
                    >
                      Sin datos para mostrar.
                    </td>
                  </tr>
                )}
                {!loading &&
                  previewRows.map((row, idx) => (
                    <tr key={idx}>
                      {columns.map((col) => (
                        <td key={col} style={{ whiteSpace: "nowrap" }}>
                          {formatCell(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withRouter(ReporteVista);
